/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import classNames from 'classnames';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import { Controller } from 'react-hook-form';
import {
  FormControl,
  FormControlLabel,
  makeStyles,
} from '@material-ui/core';
import CustomCheckbox from 'components/CustomCheckbox';
import { Trans } from 'react-i18next';
import Tooltip from '../../components/common/Tooltip';
import Button from '../../components/common/Button';
import { CustomSelectBox } from '../../components/common/CustomSelectBox';
import useEPCTCreationDialog from './useEPCTCreationDialog';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import LanguageMenu from 'layout/menu/LanguageMenu';

const useStyles = makeStyles((theme) => ({
  ...theme.customTheme,
  checkbox: {
    '& a': {
      position: 'absolute',
      width: '150px',
      paddingLeft: '4px',
    },
  },
  checkboxContainer: {
    marginTop: '1rem',
  },
  form: {
    marginTop: '2.9rem',
    [theme.breakpoints.down('md')]: {
      marginTop: '1.5rem',
    },
  },
  field: {
    marginRight: '.1rem',
    marginBottom: '3rem',
    paddingRight: '1rem',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      marginBottom: '1rem',
    },
  },
  fieldLanguageSelect: {
    marginRight: '.1rem',
    paddingRight: '1rem',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      marginBottom: '0',
    },
  },
  epctLanguageField: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  tooltip: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    right: '-24px',
  },
}));

export default function EpctCreationDialog({
  createEPCTDialog,
  procedureId,
}) {
  const classes = useStyles();
  const frontendCustomisationConfig = useConfiguration(
    ConfigResourceProps.FRONTEND_CUSTOMISATION
  );
  const {
    ROList,
    defaultOffice,
    languageList,
    userReferenceRules,
    RO,
    control,
    isGdprRequired,
    handleSubmit,
    errors,
    formState,
    t,
  } = useEPCTCreationDialog(procedureId);

  return (
    <Dialog open={createEPCTDialog.isOpen} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography component="span" variant="h2">
          {t('submission.epct.createDialog.title')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {t('submission.epct.createDialog.description')}
        <form
          id="createEPCTform"
          onSubmit={handleSubmit(createEPCTDialog.confirm)}
          className={classNames(classes.form, 'rjsf')}
        >
          <Box display="grid">
            <Controller
              control={control}
              name="fileReference"
              rules={userReferenceRules}
              defaultValue=""
              render={({ onChange, value, name }) => (
                <div className={classes.field}>
                  <TextField
                    name={name}
                    multiline
                    label={t('userReference')}
                    value={value}
                    onChange={onChange}
                    error={!!errors[name]}
                    fullWidth
                  />
                  <FormHelperText component="p" error>
                    {errors[name]?.message}
                  </FormHelperText>
                </div>
              )}
            />
            <CustomSelectBox
              name="RO"
              control={control}
              itemId="itemId"
              itemLabel="itemLabel"
              items={ROList}
              label={t('submission.epct.createDialog.RO.label')}
              defaultValue={defaultOffice || ''}
              className={classes.field}
              validationRules={{ required: true }}
            />
            <div className={classes.epctLanguageField}>
              <CustomSelectBox
                name="requestLanguage"
                control={control}
                itemId="itemId"
                itemLabel="itemLabel"
                items={languageList}
                label={t(
                  'submission.epct.createDialog.language.label'
                )}
                defaultValue=""
                disabled={RO?.length === 0}
                validationRules={{ required: true }}
                className={classes.fieldLanguageSelect}
              />
              <Tooltip
                className={classes.tooltip}
                title={t(
                  'submission.epct.createDialog.language.tooltip'
                )}
              />
            </div>

            {!!isGdprRequired && (
              <div className={classes.checkboxContainer}>
                <Controller
                  name="dataProtectionAcceptance"
                  control={control}
                  rules={{ required: true }}
                  defaultValue={false}
                  render={({ onChange }) => (
                    <FormControl>
                      <FormControlLabel
                        className={classes.checkbox}
                        label={
                          <Trans i18nKey="basic-filing.data-protection-accepted">
                            <a
                              href={
                                frontendCustomisationConfig?.resource
                                  .oepmDataProtectionLink
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              dummy content
                            </a>
                          </Trans>
                        }
                        control={
                          <CustomCheckbox
                            onChange={(e) =>
                              onChange(e.target.checked)
                            }
                          />
                        }
                      />
                    </FormControl>
                  )}
                />
              </div>
            )}
          </Box>
        </form>
        {/* </Box> */}
      </DialogContent>
      <DialogActions>
        <Button
          id="cancelEPCTCreate"
          color="default"
          onClick={createEPCTDialog.close}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          id="confirmEPCTCreate"
          form="createEPCTform"
          color="primary"
          type="submit"
          disabled={!formState?.isValid}
        >
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
